import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { PlayerYouTube } from '@/componentes/PlayerYouTube'
import { Cartao, Esqueleto, TituloSecao, Vazio } from '@/componentes/ui'
import { CHAVES, type ContextoPrograma } from '@/lib/sessao'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utilitarios'
import {
  ROTULO_CATEGORIA_VIDEO,
  ROTULO_REFEICAO,
  TIPOS_REFEICAO,
  type CategoriaVideo,
} from '@/tipos/dominio'
import type { Cardapio, Material, Refeicao, Video } from '@/tipos/tabelas'

type Secao = 'cardapios' | 'reprogramacoes' | 'exercicios' | 'materiais'

const SECOES: { id: Secao; rotulo: string; emoji: string }[] = [
  { id: 'cardapios', rotulo: 'Cardápios', emoji: '🍽️' },
  { id: 'reprogramacoes', rotulo: 'Reprogramações', emoji: '🧘' },
  { id: 'exercicios', rotulo: 'Exercícios', emoji: '💪' },
  { id: 'materiais', rotulo: 'Materiais', emoji: '📄' },
]

/** Biblioteca (seção 11). */
export function PaginaBiblioteca({ contexto }: { contexto: ContextoPrograma }) {
  const [secao, setSecao] = useState<Secao>('cardapios')
  const programaId = contexto.matricula.turma.programa.id
  const { matricula, dia } = contexto

  return (
    <div className="flex flex-col gap-5 px-5 pt-6 pb-28">
      <header>
        <h1 className="font-titulo text-2xl font-bold">Biblioteca</h1>
      </header>

      <div className="-mx-5 overflow-x-auto px-5">
        <div className="flex w-max gap-2">
          {SECOES.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSecao(item.id)}
              aria-current={secao === item.id ? 'true' : undefined}
              className={cn(
                'flex h-11 items-center gap-1.5 rounded-full px-4 font-titulo text-sm font-semibold whitespace-nowrap transition-colors',
                secao === item.id
                  ? 'bg-coral text-white'
                  : 'bg-[var(--cartao)] text-[var(--tinta-suave)]',
              )}
            >
              <span aria-hidden>{item.emoji}</span>
              {item.rotulo}
            </button>
          ))}
        </div>
      </div>

      {secao === 'cardapios' && (
        <SecaoCardapios
          programaId={programaId}
          // O cardápio é por período de duas semanas (1-2 ou 3-4), não
          // por semana individual — contexto.semana ainda é 1..4 (usada
          // pela lista de compras, que continua semanal).
          periodoAtual={Math.ceil(contexto.semana / 2)}
        />
      )}
      {secao === 'reprogramacoes' && (
        <SecaoReprogramacoes programaId={programaId} matriculaId={matricula.id} dia={dia} />
      )}
      {secao === 'exercicios' && (
        <SecaoExercicios programaId={programaId} matriculaId={matricula.id} dia={dia} />
      )}
      {secao === 'materiais' && <SecaoMateriais programaId={programaId} />}
    </div>
  )
}

// ---------------------------------------------------------------------
// Cardápios (seção 8) — texto simples, sem modo de preparo
//
// Um cardápio por período de duas semanas (1-2 ou 3-4): o mesmo vale
// para as duas semanas do período, em vez de quatro cardápios distintos.
// ---------------------------------------------------------------------

const PERIODOS = [
  { numero: 1, rotulo: 'Semanas 1 e 2' },
  { numero: 2, rotulo: 'Semanas 3 e 4' },
] as const

function SecaoCardapios({
  programaId,
  periodoAtual,
}: {
  programaId: string
  periodoAtual: number
}) {
  const [periodo, setPeriodo] = useState(periodoAtual)

  const { data, isLoading } = useQuery({
    queryKey: CHAVES.cardapio(programaId, periodo),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cardapios')
        .select('*, refeicoes(*)')
        .eq('programa_id', programaId)
        .eq('periodo', periodo)
        .order('dia_numero', { nullsFirst: true })

      if (error) throw error
      return data as (Cardapio & { refeicoes: Refeicao[] })[]
    },
    staleTime: 60 * 60 * 1000,
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        {PERIODOS.map((p) => (
          <button
            key={p.numero}
            type="button"
            onClick={() => setPeriodo(p.numero)}
            className={cn(
              'h-11 flex-1 rounded-full font-titulo text-sm font-semibold transition-colors',
              periodo === p.numero
                ? 'bg-dourado text-marrom'
                : 'bg-[var(--cartao)] text-[var(--tinta-suave)]',
            )}
          >
            {p.rotulo}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Esqueleto className="h-64" />
      ) : !data || data.length === 0 ? (
        <Vazio
          emoji="🍽️"
          titulo="Cardápio ainda não publicado"
          descricao="O cardápio deste período aparece aqui assim que for liberado."
        />
      ) : (
        data.map((cardapio) => (
          <Cartao key={cardapio.id}>
            <TituloSecao>
              {cardapio.titulo ?? (cardapio.dia_numero ? `Dia ${cardapio.dia_numero}` : 'Do período')}
            </TituloSecao>

            <div className="flex flex-col gap-3">
              {TIPOS_REFEICAO.map((tipo) => {
                const refeicoes = cardapio.refeicoes
                  .filter((r) => r.tipo === tipo)
                  .sort((a, b) => a.ordem - b.ordem)

                if (refeicoes.length === 0) return null

                return (
                  <div key={tipo}>
                    <h4 className="font-titulo text-xs font-semibold tracking-wide text-coral uppercase">
                      {ROTULO_REFEICAO[tipo]}
                    </h4>
                    {refeicoes.map((refeicao) => (
                      <p
                        key={refeicao.id}
                        className="mt-0.5 text-[15px] leading-relaxed whitespace-pre-line"
                      >
                        {refeicao.descricao}
                      </p>
                    ))}
                  </div>
                )
              })}
            </div>

            {cardapio.observacoes && (
              <p className="mt-3 rounded-xl bg-[var(--borda)] p-3 text-sm leading-relaxed text-[var(--tinta-suave)]">
                {cardapio.observacoes}
              </p>
            )}

            {cardapio.chas && (
              <div className="mt-3 rounded-xl bg-verde/10 p-3">
                <h4 className="font-titulo text-xs font-semibold tracking-wide text-verde uppercase">
                  Chás
                </h4>
                <p className="mt-0.5 text-sm leading-relaxed whitespace-pre-line">{cardapio.chas}</p>
              </div>
            )}

            {cardapio.lembretes_importantes && (
              <div className="mt-3 rounded-xl bg-dourado/15 p-3">
                <h4 className="font-titulo text-xs font-semibold tracking-wide text-marrom uppercase">
                  Lembretes importantes
                </h4>
                <p className="mt-0.5 text-sm leading-relaxed whitespace-pre-line">
                  {cardapio.lembretes_importantes}
                </p>
              </div>
            )}

            {cardapio.alimentos_proibidos && (
              <div className="mt-3 rounded-xl bg-pink/10 p-3">
                <h4 className="font-titulo text-xs font-semibold tracking-wide text-pink uppercase">
                  Alimentos proibidos
                </h4>
                <p className="mt-0.5 text-sm leading-relaxed whitespace-pre-line">
                  {cardapio.alimentos_proibidos}
                </p>
              </div>
            )}
          </Cartao>
        ))
      )}
    </div>
  )
}

// ---------------------------------------------------------------------
// Reprogramações (seção 6) e exercícios (seção 7)
// ---------------------------------------------------------------------

function useVideos(programaId: string, categorias: CategoriaVideo[]) {
  return useQuery({
    queryKey: CHAVES.videos(programaId, categorias.join(',')),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('programa_id', programaId)
        .eq('ativo', true)
        .in('categoria', categorias)
        .order('ordem')

      if (error) throw error
      return data as Video[]
    },
    staleTime: 60 * 60 * 1000,
  })
}

interface PropsSecaoVideos {
  programaId: string
  matriculaId: string
  dia: number
}

function SecaoReprogramacoes({ programaId, matriculaId, dia }: PropsSecaoVideos) {
  const { data: videos = [], isLoading } = useVideos(programaId, [
    'reprogramacao_manha',
    'reprogramacao_noite',
  ])

  if (isLoading) return <Esqueleto className="h-64" />

  if (videos.length === 0) {
    return (
      <Vazio
        emoji="🧘"
        titulo="Reprogramações ainda não publicadas"
        descricao="As sessões da manhã e da noite aparecem aqui assim que forem liberadas."
      />
    )
  }

  return <ListaVideos videos={videos} matriculaId={matriculaId} dia={dia} />
}

function SecaoExercicios({ programaId, matriculaId, dia }: PropsSecaoVideos) {
  const { data: videos = [], isLoading } = useVideos(programaId, [
    'cardio',
    'alongamento',
    'fortalecimento',
  ])

  if (isLoading) return <Esqueleto className="h-64" />

  if (videos.length === 0) {
    return (
      <Vazio
        emoji="💪"
        titulo="Exercícios ainda não publicados"
        descricao="Cardio, alongamento e fortalecimento aparecem aqui assim que forem liberados."
      />
    )
  }

  return <ListaVideos videos={videos} matriculaId={matriculaId} dia={dia} />
}

function ListaVideos({
  videos,
  matriculaId,
  dia,
}: {
  videos: Video[]
  matriculaId: string
  dia: number
}) {
  const categorias = [...new Set(videos.map((v) => v.categoria))]

  return (
    <div className="flex flex-col gap-6">
      {categorias.map((categoria) => (
        <section key={categoria}>
          <TituloSecao>{ROTULO_CATEGORIA_VIDEO[categoria]}</TituloSecao>
          <div className="flex flex-col gap-4">
            {videos
              .filter((v) => v.categoria === categoria)
              .map((video) => (
                <CartaoVideo
                  key={video.id}
                  video={video}
                  matriculaId={matriculaId}
                  dia={dia}
                />
              ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function CartaoVideo({
  video,
  matriculaId,
  dia,
}: {
  video: Video
  matriculaId: string
  dia: number
}) {
  // Registrar a visualização é o que desbloqueia a conquista "primeiro
  // exercício" — a regra roda num trigger sobre videos_assistidos.
  async function registrarVisualizacao() {
    await supabase.from('videos_assistidos').upsert(
      { matricula_id: matriculaId, video_id: video.id, dia_numero: dia },
      { onConflict: 'matricula_id,video_id', ignoreDuplicates: true },
    )
  }

  return (
    <article className="flex flex-col gap-2">
      <PlayerYouTube
        youtubeId={video.youtube_id}
        titulo={video.titulo}
        aoIniciar={() => void registrarVisualizacao()}
      />
      <div>
        <h3 className="font-titulo text-[15px] font-semibold">{video.titulo}</h3>
        {video.descricao && (
          <p className="mt-0.5 text-sm leading-snug text-[var(--tinta-apagada)]">
            {video.descricao}
          </p>
        )}
      </div>
    </article>
  )
}

// ---------------------------------------------------------------------
// Materiais complementares
// ---------------------------------------------------------------------

function SecaoMateriais({ programaId }: { programaId: string }) {
  const { data: materiais = [], isLoading } = useQuery({
    queryKey: ['materiais', programaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('materiais')
        .select('*')
        .eq('programa_id', programaId)
        .eq('ativo', true)
        .order('ordem')

      if (error) throw error
      return data as Material[]
    },
    staleTime: 60 * 60 * 1000,
  })

  if (isLoading) return <Esqueleto className="h-40" />

  if (materiais.length === 0) {
    return (
      <Vazio
        emoji="📄"
        titulo="Nenhum material ainda"
        descricao="Documentos e materiais de apoio aparecem aqui."
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {materiais.map((material) => (
        <a
          key={material.id}
          href={material.url ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-2xl bg-[var(--cartao)] p-4 shadow-[var(--shadow-suave)]"
        >
          <span className="text-2xl" aria-hidden>
            📄
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-titulo text-[15px] font-semibold">{material.titulo}</span>
            {material.descricao && (
              <span className="block text-sm text-[var(--tinta-apagada)]">
                {material.descricao}
              </span>
            )}
          </span>
          <span className="text-[var(--tinta-apagada)]" aria-hidden>
            ›
          </span>
        </a>
      ))}
    </div>
  )
}
